import { useMemo } from "react";

import {
  WORKFLOW_EDITOR_PAGE_CONSTANTS,
  WorkflowEditorPageKey,
} from "components/WorkflowEditor/WorkflowEditorProvider/WorkflowEditor.store";
import { useWorkflowEditorContext } from "components/WorkflowEditor/WorkflowEditorProvider/WorkflowEditorProvider";

/**
 * Hook to derive tool editor timeline status from store state
 */
export const useWorkflowEditorTimelineStatus = (
  page: WorkflowEditorPageKey,
) => {
  const hasTables = useWorkflowEditorContext((s) => s.tableInfo !== undefined);
  const visitedPages = useWorkflowEditorContext((s) => s.visitedPages);

  const hasKeyAndName = useWorkflowEditorContext(
    (s) => s.tableProps.key !== "" && s.tableProps.name !== "",
  );

  const timelineStatusByPage = useMemo(() => {
    const map: Record<
      WorkflowEditorPageKey,
      {
        status:
          | "current"
          | "warning"
          | "disabled"
          | "incomplete"
          | "loading"
          | "danger"
          | "complete";
        isDisabled: boolean;
      }
    > = {
      TABLES: { status: "disabled", isDisabled: false },
      SOURCES: { status: "disabled", isDisabled: true },
      OUTPUTS: { status: "disabled", isDisabled: true },
      NAME: { status: "disabled", isDisabled: true },
    };

    // if no tables, then only tables should be enabled
    if (!hasTables) {
      map["TABLES"] = { status: "incomplete", isDisabled: false };
      return map;
    }

    // if you have visited a page, the next page is enabled with a status of >= incomplete
    visitedPages.forEach((visitedPage) => {
      const visitedPageNumber =
        WORKFLOW_EDITOR_PAGE_CONSTANTS.PAGE_KEY_TO_PAGE_NUMBER[visitedPage];
      if (
        visitedPageNumber !== WORKFLOW_EDITOR_PAGE_CONSTANTS.LAST_PAGE_NUMBER
      ) {
        const nextPageNumber = (visitedPageNumber +
          1) as (typeof WORKFLOW_EDITOR_PAGE_CONSTANTS.WORKFLOW_EDITOR_PAGES)[number]["pageNumber"];
        const nextPageKey =
          WORKFLOW_EDITOR_PAGE_CONSTANTS.PAGE_NUMBER_TO_PAGE_KEY[
            nextPageNumber
          ];
        map[nextPageKey] = { isDisabled: false, status: "incomplete" };
      }
    });

    const PAGES_THAT_DONT_REQUIRE_ACTIONS = new Set<WorkflowEditorPageKey>([
      "TABLES",
      "SOURCES",
      "OUTPUTS",
    ] as const);
    // visited pages that don't require action are complete
    visitedPages.forEach((visitedPage) => {
      if (PAGES_THAT_DONT_REQUIRE_ACTIONS.has(visitedPage)) {
        map[visitedPage] = { ...map[visitedPage], status: "complete" };
      }
    });

    if (hasKeyAndName) {
      map["NAME"] = { ...map["NAME"], status: "complete" };
    }

    map[page] = { status: "current", isDisabled: false };

    return map;
  }, [hasKeyAndName, hasTables, page, visitedPages]);

  return timelineStatusByPage;
};
